import React, { useEffect, useState } from 'react';
import Button from 'components/Form/Button';
import TextField from 'components/Form/TextField';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { passwordStrengthVariables } from 'utils';
import { UpdatePasswordDto } from 'apps/user/types/on-boarding.type';
import { useUpdatePassword } from 'common/queries-and-mutations/user';
import { toast } from 'react-toastify';
import { Alert } from 'components/Toast';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from 'store/hooks';
import { logout } from 'thunks/account-thunk';

export default function UpdatePassword() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { mutate, isSuccess, isLoading, isError } = useUpdatePassword();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const formik = useFormik({
    initialValues: {
      oldPassword: '',
      password: ''
    },
    validationSchema: Yup.object({
      password: Yup.string()
        .required('Password is required').nullable()
        .matches(
          // eslint-disable-next-line no-useless-escape
          /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[!@#$%^&*()_+{}\[\]:;<>,.?~|]).{8,40}$/,
          'Password must have at least one uppercase, one lowercase, one number, one character and be at least 8 characters long'
        ),
      oldPassword: Yup.string().required('Old password is required').nullable()
    }),
    onSubmit: (values, { setFieldTouched }) => {
      setIsSubmitting(true);
      for (const key in values) {
        setFieldTouched(key, true);
      }
      handleSubmit(values);
    }
  });

  const handleSubmit = (updatePassword: UpdatePasswordDto) => {
    mutate(updatePassword);
  };

  const handleLogout = () => {
    dispatch(logout());
    navigate('/', { replace: true });
  };

  useEffect(() => {
    if (isSuccess && !isError) {
      toast(<Alert type="success" message="Password Successfully Updated" />);
      setIsSubmitting(false);
      handleLogout();
    }
    if (isError) {
      setIsSubmitting(false);
    }
  }, [isSuccess, isError]);

  return (
    <div className="flex flex-col rounded-lg bg-white p-6">
      <p className="text-sm font-medium text-primary-800">Update Password</p>
      <form
        autoComplete="false"
        className="mt-5"
        onSubmit={formik.handleSubmit}
      >
        <div className="flex flex-col items-center gap-5">
          <TextField
            name="oldPassword"
            placeholder="Old Password"
            value={formik.values.oldPassword}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            label=""
            error={formik.touched.oldPassword ? formik.errors.oldPassword : ''}
            isRequired
            size="lg"
            className="h-[2.8rem] rounded-lg border-[1.5px] border-gray-300"
          />
          <TextField
            name="password"
            type="password"
            placeholder="New Password"
            value={formik.values.password}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            label=""
            error={formik.touched.password ? formik.errors.password : ''}
            isRequired
            size="lg"
            className="rounded-2xl border-[1.5px] border-gray-300"
            hasStrengthIndicator={
              passwordStrengthVariables(formik.values).hasStrengthIndicator
            }
            totalStrengthConditions={
              passwordStrengthVariables(formik.values).totalStrengthConditions
            }
            strengthConditionsFulfilled={
              passwordStrengthVariables(formik.values)
                .strengthConditionsFulfilled
            }
          />
        </div>

        <div className="mt-5 flex items-end justify-end gap-8">
          <Button
            variant="primary"
            size="lg"
            className="mt-1 h-[2.6rem] w-[9rem] rounded"
            label="Save"
            type="submit"
            disabled={!formik.isValid}
            loading={isSubmitting || isLoading}
          ></Button>
        </div>
      </form>
    </div>
  );
}
